import React, { useEffect, useState } from "react";
import {
  CardBody,
  Col,
  Row,
  Input,
  Label,
  Button,
  FormFeedback,
  Alert,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import imageLogo from "../../../assets/orderingSystem/login-bg.jpg";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";
import { forgotPassword } from "../redux/slices/customerAuthSlice";
const arrow =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 50'%3E%3Cpath d='M10 25 Q50 5 80 25' stroke='white' fill='none' stroke-width='3' stroke-linecap='round'/%3E%3Cpolygon points='80,25 70,15 75,25 70,35' fill='white'/%3E%3C/svg%3E";

const CustomerForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orgcode } = useParams();
  const { loading, error } = useSelector((s) => s.customerAuth || {});
  const [errorMsg, setErrorMsg] = useState(null);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { emailAddress: "" },
    validationSchema: Yup.object({
      emailAddress: Yup.string()
        .email("Please enter a valid email")
        .required("Please enter your email"),
    }),
    onSubmit: async (values) => {
      try {
        setErrorMsg(null);
        const res = await dispatch(forgotPassword(values));
        if (res?.type && res.type.includes("fulfilled")) {
          // store emailForOtp is handled by slice; navigate to verify otp
          navigate(`/${orgcode}/verify-otp`);
        } else {
          const msg =
            res?.payload?.message ||
            res?.payload ||
            "Failed to send reset code";
          setErrorMsg(msg);
        }
      } catch (err) {
        setErrorMsg("Failed to send reset code. Please try again.");
        console.error("Forgot password error:", err);
      }
    },
  });

  useEffect(() => {
    if (errorMsg) {
      const t = setTimeout(() => setErrorMsg(null), 4000);
      return () => clearTimeout(t);
    }
  }, [errorMsg]);

  document.title = "Forgot Password - Ordering System";

  return (
    <React.Fragment>
      <div className="vh-100">
        <Row className="h-100 g-0">
          <Col
            lg={{ size: 5, order: 2 }}
            md={{ size: 5, order: 2 }}
            xs={{ size: 12, order: 1 }}
            sm={{ size: 12, order: 1 }}
            xl={{ size: 6, order: 2 }}
            className="h-100 d-none d-sm-none d-md-block d-lg-block"
            style={{ backgroundColor: "#0f403b" }}
          >
            <div className="py-5 d-flex flex-column justify-content-center align-items-center h-100">
              <CardBody
                className="p-4"
                style={{ maxWidth: 480, width: "100%" }}
              >
                <div className="text-center mb-4">
                  <h5 className="text-white">Forgot your password?</h5>
                  <p className="text-white-50">
                    Enter your account email and we'll send a verification code.
                  </p>
                </div>

                {errorMsg && <Alert color="danger">{errorMsg}</Alert>}
                {error && (
                  <Alert color="danger">
                    {typeof error === "string" ? error : JSON.stringify(error)}
                  </Alert>
                )}

                <div className="p-2 py-3">
                  <div
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                    }}
                  >
                    <div className="mb-3">
                      <Label className="text-white mb-2 d-block">Email</Label>
                      <Input
                        name="emailAddress"
                        className="form-control p-3"
                        placeholder="Enter email"
                        type="email"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.emailAddress || ""}
                        invalid={
                          validation.touched.emailAddress &&
                          validation.errors.emailAddress
                            ? true
                            : false
                        }
                      />
                      {validation.touched.emailAddress &&
                      validation.errors.emailAddress ? (
                        <FormFeedback type="invalid">
                          {validation.errors.emailAddress}
                        </FormFeedback>
                      ) : null}
                    </div>

                    <div className="mb-3">
                      <Button
                        disabled={loading}
                        color="success"
                        className="btn btn-success w-100"
                        type="button"
                        onClick={() => validation.handleSubmit()}
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" /> Sending...
                          </>
                        ) : (
                          "Send Verification Code"
                        )}
                      </Button>
                    </div>

                    <div className="text-center text-white">
                      <p className="mb-0">
                        Remembered your password?{" "}
                        <Link
                          to={`/${orgcode}/customer-login`}
                          className="text-warning"
                        >
                          Sign in
                        </Link>
                      </p>
                    </div>
                  </div>
                </div>
              </CardBody>
            </div>
          </Col>

          <Col
            lg={{ size: 7, order: 1 }}
            md={{ size: 7, order: 1 }}
            xl={{ size: 6, order: 1 }}
            xs={{ size: 12, order: 2 }}
            sm={{ size: 12, order: 2 }}
            className="position-relative h-100 d-none d-sm-none d-md-flex d-lg-flex"
          >
            <img
              src={imageLogo}
              alt="Background"
              className="w-100 h-100"
              style={{ objectFit: "cover" }}
            />
            <div className="login-overlay d-flex flex-column justify-content-center align-items-start position-absolute w-100 h-100">
              <div className="overlay-content w-100 px-4">
                <h1 className="fw-bold display-5 mb-4 text-white">
                  Public Ordering System
                </h1>
                <p className="text-white-50">
                  Fast, simple and secure ordering for customers.
                </p>
              </div>
            </div>
          </Col>

          <Col
            lg={{ size: 5, order: 2 }}
            xs={{ size: 12, order: 1 }}
            sm={{ size: 12, order: 1 }}
            xl={{ size: 6, order: 2 }}
            className="h-100 d-block d-sm-block d-md-none d-lg-none position-relative"
          >
            <img
              src={imageLogo}
              alt="Public Ordering System"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            />
            <div
              className="py-5 d-flex flex-column justify-content-center align-items-center h-100"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CardBody
                className="p-4"
                style={{ maxWidth: 480, width: "100%" }}
              >
                <div className="text-center mb-4">
                  <h5 className="text-white">Forgot your password?</h5>
                  <p className="text-white-50">
                    Enter your account email and we'll send a verification code.
                  </p>
                </div>

                {errorMsg && <Alert color="danger">{errorMsg}</Alert>}
                {error && (
                  <Alert color="danger">
                    {typeof error === "string" ? error : JSON.stringify(error)}
                  </Alert>
                )}

                <div className="p-2 py-3">
                  <div
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                    }}
                  >
                    <div className="mb-3">
                      <Label className="text-white mb-2 d-block">Email</Label>
                      <Input
                        name="emailAddress"
                        className="form-control p-3"
                        placeholder="Enter email"
                        type="email"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.emailAddress || ""}
                        invalid={
                          validation.touched.emailAddress &&
                          validation.errors.emailAddress
                            ? true
                            : false
                        }
                      />
                      {validation.touched.emailAddress &&
                      validation.errors.emailAddress ? (
                        <FormFeedback type="invalid">
                          {validation.errors.emailAddress}
                        </FormFeedback>
                      ) : null}
                    </div>

                    <div className="mb-3">
                      <Button
                        disabled={loading}
                        color="success"
                        className="btn btn-success w-100"
                        type="button"
                        onClick={() => validation.handleSubmit()}
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" /> Sending...
                          </>
                        ) : (
                          "Send Verification Code"
                        )}
                      </Button>
                    </div>

                    <div className="text-center text-white">
                      <p className="mb-0">
                        Remembered your password?{" "}
                        <Link
                          to={`/${orgcode}/customer-login`}
                          className="text-warning"
                        >
                          Sign in
                        </Link>
                      </p>
                    </div>
                  </div>
                </div>
              </CardBody>

              <footer className="mt-auto w-100">
                <div className="container">
                  <Row>
                    <Col xs={12}>
                      <div className="text-center">
                        <p className="mb-0 text-muted">
                          &copy; {new Date().getFullYear()} Danfe Solution
                        </p>
                      </div>
                    </Col>
                  </Row>
                </div>
              </footer>
            </div>
          </Col>
        </Row>
      </div>

      <style>{`
        .login-overlay { top: 0; left: 0; }
        .form-control { border-radius: 8px; border: 1px solid #ddd; }
        .form-control:focus { border-color: #28a745; }
        .btn-success {  border-color: #28a745; border-radius: 8px; font-weight: 600; }
      `}</style>
    </React.Fragment>
  );
};

export default CustomerForgotPassword;
